'use client';

import type { NavigationItem } from '@dealerportal/common';
import Link from 'next/link';
import { useContext, useEffect, useRef, useState } from 'react';
import { NavDesktopMenuDropdown } from './NavDesktopMenuDropdown';
import { NavigationContext } from './Navigation';

export function NavDesktopMenuItems({
  items,
  depthLevel,
  className,
}: {
  items: NavigationItem;
  depthLevel: number;
  className?: string;
}) {
  const [dropdown, setDropdown] = useState<boolean>(false);
  const [subItemSelected, setSubItem] = useState<boolean>(false);
  const subItemRef = useRef<HTMLLIElement>(null);

  const navClassNames = useContext(NavigationContext);

  useEffect(() => {
    const handler = (event: MouseEvent | TouchEvent) => {
      if (
        dropdown &&
        subItemRef.current &&
        !subItemRef.current.contains(event.target as Node)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [dropdown]);

  const subItemSetting = subItemSelected ? 'ui-hidden' : 'ui-block';

  const modifiedClassName =
    depthLevel === 0 ? `${className}_item` : `${className}_subitem`;

  const dropdownClassName = depthLevel > 0 ? '!ui-px-4 !ui-py-2' : '';

  const twLinkClassName = `${modifiedClassName} ${dropdownClassName} ui-w-full ui-flex ui-items-center ui-justify-between !ui-text-left ui-no-underline hover:!ui-no-underline`;

  const itemClassName =
    depthLevel === 0
      ? `nav__item ${navClassNames.itemclass}`
      : depthLevel === 1
        ? `nav__child-item ${navClassNames.childitemclass}`
        : `nav__grandchild-item ${navClassNames.grandchilditemclass}`;

  const linkClassName =
    depthLevel === 0
      ? `nav__link ${navClassNames.linkclass}`
      : depthLevel === 1
        ? `nav__child-link ${navClassNames.childlinkclass}`
        : `nav__grandchild-link ${navClassNames.grandchildlinkclass}`;

  const show = items.isVisible && !items.hideInMenu;

  if (!show) return null;

  const isLink = Boolean(items.alias);
  const Tag = isLink
    ? (Link as React.ElementType)
    : ('div' as React.ElementType);
  const sharedTagProps = {
    className: `${linkClassName} ${subItemSetting} ${twLinkClassName}`,
    ...(depthLevel > 0 ? { 'aria-hidden': 'true', tabIndex: -1 } : {}),
  };
  const sharedChildTagProps = {
    className: `ui-cursor-pointer ${linkClassName} ${twLinkClassName}`,
    ...(depthLevel > 0 ? { 'aria-hidden': 'true', tabIndex: -1 } : {}),
  };
  const linkProps = isLink
    ? {
        href: items.alias,
        target: items.target,
        prefetch: false,
      }
    : {};
  const childLinkProps = isLink
    ? {
        href: items.alias || '',
        target: items.target,
      }
    : {};

  return (
    <li
      key={items._id}
      className={`${itemClassName} menuItems ui-relative ui-pl-0 ui-m-0 ui-flex ${
        items?.children && items.hasVisibleChildren
          ? '--has-children'
          : '--no-children'
      }`}
      ref={subItemRef}
      onMouseEnter={() => setDropdown(true)}
      onMouseLeave={() => setDropdown(false)}
      aria-hidden={depthLevel === 0 ? undefined : 'true'} // Only hide non-root items
    >
      {items?.children && items.hasVisibleChildren && show ? (
        <>
          <div
            className="nav__link-wrapper menus ui-w-full"
            aria-haspopup="menu"
            role="button"
            aria-expanded={dropdown}
            onClick={() => {
              setDropdown(!dropdown);
              setSubItem(!subItemSelected);
            }}
            {...(depthLevel > 0 && { 'aria-hidden': 'true', tabIndex: -1 })}
          >
            <Tag {...sharedChildTagProps} {...childLinkProps}>
              {items.name}
              {depthLevel > 0 && (
                <svg
                  className="ui-w-2"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  aria-hidden="true"
                >
                  <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8 12.5 45.3 0l192 192z" />
                </svg>
              )}
            </Tag>
          </div>
          <NavDesktopMenuDropdown
            submenus={items.children}
            dropdown={dropdown}
            depthLevel={depthLevel}
            className={className}
          />
        </>
      ) : (
        show && (
          <Tag {...sharedTagProps} {...linkProps}>
            {items.name}
          </Tag>
        )
      )}
    </li>
  );
}
export default NavDesktopMenuItems;
